import { getMegaColumns, getSoorten, soortSlug, type MegaColumn } from "@/lib/catalog";
import { BENG_VARIANTS } from "@/features/benglas/benglas-page-data";

/**
 * Gedeelde navigatie-data voor de site-chrome: de categorie-balk, het desktop-cascademenu
 * (SiteLayout) én het mobiele drill-down-menu (MobileMenu). Eén bron zodat beide menu's
 * altijd dezelfde categorieën/subcategorieën tonen.
 */

// Categorie-balk onder de hoofdbalk — glas-eerst, dan beslag & benodigdheden.
// Alle items klikbaar (lege categorieën tonen een "binnenkort"-pagina).
export const CATEGORIES: { slug: string; label: string; to: string }[] = [
  { slug: "enkel-glas", label: "Enkel glas", to: "/glas-op-maat" },
  { slug: "isolatieglas", label: "Isolatieglas", to: "/categorie/isolatieglas" },
  { slug: "vacuumglas", label: "Vacuümglas", to: "/vacuumglas" },
  { slug: "installatieglas", label: "Installatieglas", to: "/categorie/installatieglas" },
  { slug: "douche-en-bad", label: "Douche & Bad", to: "/categorie/douche-en-bad" },
  { slug: "interieurbeglazing", label: "Interieurbeglazing", to: "/categorie/interieurbeglazing" },
  { slug: "vitrine-en-meubelbeslag", label: "Vitrine- en meubelbeslag", to: "/categorie/vitrine-en-meubelbeslag" },
  { slug: "balustrade", label: "Balustrade", to: "/categorie/balustrade" },
  { slug: "glasartikelen", label: "Glasartikelen", to: "/categorie/glasartikelen" },
  { slug: "ventilatieroosters", label: "Ventilatieroosters", to: "/categorie/ventilatieroosters" },
];

// Mega-menu: linker-rail met hoofdcategorieën (2 groepen), rechts de
// subcategorieën/soorten/productgroepen van de actieve categorie.
export const RAIL: { slug: string; label: string; to: string; group: "glas" | "beslag" }[] = [
  { slug: "enkel-glas", label: "Enkel glas", to: "/glas-op-maat", group: "glas" },
  { slug: "isolatieglas", label: "Isolatieglas", to: "/categorie/isolatieglas", group: "glas" },
  { slug: "vacuumglas", label: "Vacuümglas", to: "/vacuumglas", group: "glas" },
  { slug: "installatieglas", label: "Installatieglas", to: "/categorie/installatieglas", group: "glas" },
  // Beslag-volgorde 1-op-1 met Timmers (webshopSortIndex).
  { slug: "douche-en-bad", label: "Douche & Bad", to: "/categorie/douche-en-bad", group: "beslag" },
  { slug: "interieurbeglazing", label: "Interieurbeglazing", to: "/categorie/interieurbeglazing", group: "beslag" },
  { slug: "vitrine-en-meubelbeslag", label: "Vitrine- en meubelbeslag", to: "/categorie/vitrine-en-meubelbeslag", group: "beslag" },
  { slug: "balustrade", label: "Balustrade", to: "/categorie/balustrade", group: "beslag" },
  { slug: "glasartikelen", label: "Glasartikelen", to: "/categorie/glasartikelen", group: "beslag" },
  { slug: "ventilatieroosters", label: "Ventilatieroosters", to: "/categorie/ventilatieroosters", group: "beslag" },
];
export const FIRST_RAIL = RAIL[0]!;
export const GROUP_LABEL: Record<"glas" | "beslag", string> = { glas: "Glas op maat", beslag: "Beslag & artikelen" };

// Glas-panelen zijn deels handmatig: nieuwe categorieën (veiligheidsglas, geëmaill./
// gelakt, ramen) bestaan nog niet in de feed → "binnenkort"-pagina's.
//
// LAZY (01-08-2026): dit stond als module-scope const, maar de catalogus-seed laadt
// tegenwoordig async (loadCatalogData in lib/catalog) — op importmoment zou getSoorten()
// nog leeg zijn en bleven alle foto's voorgoed undefined. Nu bouwt buildGlassPanes() de
// map bij de eerste échte aanroep (ná de load, want menu's renderen pas dan) en cachet
// alleen een resultaat mét data.
function buildGlassPanes(): Record<string, MegaColumn[]> {
  const SOORT_IMG: Record<string, string> = {};
  for (const s of getSoorten()) if (s.img) SOORT_IMG[soortSlug(s)] = s.img;
  const glasFoto = SOORT_IMG["floatglas"] ?? Object.values(SOORT_IMG)[0];
  return {
  "enkel-glas": [
    { title: "Floatglas",        titleTo: "/glas-op-maat/floatglas",        image: SOORT_IMG["floatglas"] ?? glasFoto,        links: [] },
    { title: "Mat glas",         titleTo: "/glas-op-maat/mat-glas",        image: SOORT_IMG["mat-glas"] ?? glasFoto,        links: [] },
    {
      title: "Veiligheidsglas",
      titleTo: "/categorie/veiligheidsglas",
      image: glasFoto,
      links: [
        { label: "Gehard glas", to: "/glas-op-maat/gehard-glas" },
        { label: "Gelaagd glas", to: "/glas-op-maat/gelaagd-glas" },
        { label: "Gehard-gelaagd glas", to: "/glas-op-maat/gehard-gelaagd-glas" },
        { label: "Beloopbaar glas", to: "/glas-op-maat/beloopbaar-glas" },
      ],
    },
    { title: "Figuurglas",       titleTo: "/glas-op-maat/figuurglas",       image: SOORT_IMG["figuurglas"] ?? glasFoto,       links: [] },
    { title: "Draadglas", titleTo: "/glas-op-maat/draadglas", image: SOORT_IMG["draadglas"] ?? SOORT_IMG["figuurglas"] ?? glasFoto, links: [] },
    { title: "Spiegels op maat", titleTo: "/glas-op-maat/spiegels-op-maat", image: SOORT_IMG["spiegels-op-maat"] ?? glasFoto, links: [] },
    {
      title: "Gekleurd glas",
      titleTo: "/categorie/gekleurd-glas",
      image: SOORT_IMG["lacobel-safe"] ?? SOORT_IMG["lacobel"] ?? glasFoto,
      links: [
        { label: "Geëmailleerd glas", to: "/glas-op-maat/geemailleerd-glas" },
        { label: "Lacobel-Safe", to: "/glas-op-maat/lacobel-safe" },
        { label: "Lacobel-T", to: "/glas-op-maat/lacobel-t" },
        { label: "Matelac-Safe", to: "/glas-op-maat/matelac-safe" },
        { label: "Matelac-T", to: "/glas-op-maat/matelac-t" },
        { label: "Gehard gelakt glas", to: "/glas-op-maat/gehard-gelakt-glas" },
      ],
    },
    {
      title: "Voorzetramen",
      titleTo: "/categorie/glasartikelen/voorzetramen",
      // Eigen WoodView-productfoto in de nav (de artikelen zelf houden hun eigen foto's).
      image: "/glas-foto/voorzetraam-woodview.webp",
      links: [
        { label: "QualiView 4 mm", to: "/artikel/qualiview-voorzetraam-4-mm" },
        { label: "QualiView 6 mm", to: "/artikel/qualiview-voorzetraam-6-mm" },
        { label: "QualiView 33.1 gelaagd", to: "/artikel/qualiview-voorzetraam-331-gelaagd" },
        { label: "WoodView 4 mm", to: "/artikel/woodview-voorzetraam-4-mm" },
      ],
    },
    { title: "Klep-, val- en draairamen", titleTo: "/categorie/klep-val-draairamen", soon: true, image: glasFoto, links: [] },
  ],
  // Foto's uit ons eigen assortiment (Solid Shower-serie) i.p.v. 3× dezelfde glasplaat-op-tafel:
  // installatieglas kóóp je zelden om het glas zelf, je koopt de douche die eruit komt. Bewust
  // niet de soort-foto van installatieglas hergebruikt — die hoort in de configurator te blijven
  // waar je de glassóórt kiest.
  installatieglas: [
    { title: "Installatieglas", titleTo: "/glas-op-maat/installatieglas", image: "/douche-foto/installatieglas-paneel.webp", links: [] },
    { title: "Douchedeuren samenstellen", titleTo: "/douche/samenstellen", image: "/douche-foto/douchedeur-hoek.webp", links: [] },
    { title: "Douchecabines (kant-en-klaar)", titleTo: "/douche/opstellingen", image: "/douche-foto/douchecabine-compleet.webp", links: [] },
  ],
  isolatieglas: [
    { title: "Dubbel glas", titleTo: "/isolatieglas/samenstellen", image: "/glas-foto/isolatieglas-dubbel.webp", links: [] },
    { title: "Triple glas", titleTo: "/triple-glas/samenstellen", image: "/glas-foto/isolatieglas-triple.webp", links: [] },
  ],
  vacuumglas: BENG_VARIANTS.map((v) => ({ title: v.label, titleTo: v.path, image: v.image, links: [] })),
  };
}

let glassPanesCache: Record<string, MegaColumn[]> | null = null;
function glassPanes(): Record<string, MegaColumn[]> {
  if (glassPanesCache) return glassPanesCache;
  const panes = buildGlassPanes();
  // Alleen cachen als de catalogus geladen was (anders zonder foto's opgebouwd).
  if (getSoorten().length > 0) glassPanesCache = panes;
  return panes;
}

// Handmatige 'binnenkort'-groepen bovenop de Timmers-feed (items die wij toevoegen
// maar nog niet in de feed zitten). Worden achter de feed-groepen gemerged.
const EXTRA_PANES: Record<string, MegaColumn[]> = {
  interieurbeglazing: [
    { title: "Steellookdeuren", titleTo: "/categorie/steellookdeuren", soon: true, links: [] },
  ],
  "douche-en-bad": [
    { title: "Douchecabines (kant-en-klaar)", titleTo: "/douche/opstellingen", links: [] },
    { title: "Douchewand met deur — 3D samensteller", titleTo: "/douche/samenstellen", links: [] },
  ],
};

/** Subcategorie-kolommen van een hoofdcategorie (handmatige panes vóór de feed). */
export function sectionsFor(slug: string): MegaColumn[] {
  return glassPanes()[slug] ?? [...getMegaColumns(slug), ...(EXTRA_PANES[slug] ?? [])];
}
